import { Analytics } from '@cobliteam/analytics';
import {
  type CardProviderOption,
  type CreateIntegrationRequestBody,
  FuelCardRequester,
  type FuelTransaction,
  GraphQLFuelCardRequester,
  type IntegrationRequestResponse,
  NewFuelCardRequester,
  type TransactionsReportFilters,
} from '@cobliteam/api-lib';
import type {
  FuelCardAssociationResponse,
  ImportFuelTransactionsBatchInput,
  UpsertVehicleFuelConsumptionGoalInput,
  ValidateFuelTransactionsImportInput,
  ValidateFuelTransactionsImportResponse,
} from '@cobliteam/api-lib/src/__generated__/graphql';
import type { UpdateTransactionRequest } from '@cobliteam/api-lib/src/dashboard/fuelCard/Definitions/APITypes';
import type {
  DateRange,
  FuelTransactionBodyContent,
  PagedTransactionStatsByVehicle,
  PagedTransactions,
  TransactionStatsByVehicleSummary,
  TransactionStatsByVehicleSummaryByPeriod,
  TransactionSummaryBodyContent,
  TransactionSummaryBodyContentByPeriodType,
  TransactionsAvgFuelConsumptionAndCostPerKm,
  TransactionsHistoryBodyContent,
  TransactionsSummary,
  TransactionsSumTotalCostByFuelType,
  UUID,
} from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import { Environment } from '@cobliteam/react-environments';
import { format } from '@cobliteam/utils/date';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { QUERY_KEY as VEHICLES_QUERY_KEY } from '../useEntity/useVehicle';
import { FUEL_TRANSACTION_QUERY_KEY, FUEL_TRANSACTIONS_QUERY_KEY, invalidateFuelTransactionQueries } from './invalidation';
import { FLEET_FUEL_INTEGRATIONS_QUERY_KEY } from './useFleetFuelIntegrations';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getFuelCardRequester = () => new FuelCardRequester({ host: env.herbieApiHost });
const getNewFuelCardRequester = () => new NewFuelCardRequester({ host: env.apiHost });
const getGraphQLFuelCardRequester = () => new GraphQLFuelCardRequester();

const FUELCARD_ACTIVE_QUERY_KEY = 'fuelCardActive';
const FUELCARD_INACTIVE_QUERY_KEY = 'fuelCardInactive';

export type PhotoImportItemProgress = { status: 'uploading' } | { status: 'success'; id: string } | { status: 'error'; error: unknown };

export type PhotoImportItemResult = { ok: true; id: string } | { ok: false; error: unknown };

export interface PhotoImportVariables {
  inputs: FuelTransactionBodyContent[];
  /** Optional odometer photos (one per input by index). When present, the photo is base64-encoded
   *  and shipped on the same JSON body as the receipt. */
  odometerFiles?: (File | undefined)[];
  onItemProgress?: (index: number, progress: PhotoImportItemProgress) => void;
}

const useFuelCard = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('fuel-card');

  const useGetActiveFuelCardAssociations = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<FuelCardAssociationResponse[]>({
      queryKey: [FUELCARD_ACTIVE_QUERY_KEY],
      queryFn: () => getGraphQLFuelCardRequester().getFuelCardAssociations('active'),
      retry: false,
      enabled: true,
      refetchOnWindowFocus: false,
    });
  };

  const useGetInactiveFuelCardAssociations = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<FuelCardAssociationResponse[]>({
      queryKey: [FUELCARD_INACTIVE_QUERY_KEY],
      queryFn: () => getGraphQLFuelCardRequester().getFuelCardAssociations('inactive'),
      retry: false,
      enabled: true,
      refetchOnWindowFocus: false,
    });
  };

  const useFuelTransaction = (transactionId: string) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<FuelTransaction>({
      queryKey: [FUEL_TRANSACTION_QUERY_KEY, transactionId],
      queryFn: () => getNewFuelCardRequester().getFuelTransaction(transactionId),
    });
  };

  const useTransactionsHistory = (input: TransactionsHistoryBodyContent) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<PagedTransactions>({
      queryKey: [FUEL_TRANSACTIONS_QUERY_KEY, input],
      queryFn: () => getNewFuelCardRequester().getTransactionsHistory(input),
    });
  };

  const useTransactionsSummary = (input: TransactionSummaryBodyContent, options?: { enabled?: boolean }) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<TransactionsSummary>({
      queryKey: [FUEL_TRANSACTIONS_QUERY_KEY, 'summary', input],
      queryFn: () => getNewFuelCardRequester().getTransactionsSummary(input),
      enabled: options?.enabled ?? true,
    });
  };

  const useTransactionStatsByVehicle = (input: TransactionsHistoryBodyContent, options?: { enabled?: boolean }) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<PagedTransactionStatsByVehicle>({
      queryKey: [FUEL_TRANSACTIONS_QUERY_KEY, 'stats-by-vehicle', input],
      queryFn: () => getNewFuelCardRequester().getTransactionStatsByVehicle(input),
      enabled: options?.enabled ?? true,
    });
  };

  const useTransactionStatsByVehicleSummary = (input: TransactionSummaryBodyContent, options?: { enabled?: boolean }) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<TransactionStatsByVehicleSummary>({
      queryKey: [FUEL_TRANSACTIONS_QUERY_KEY, 'stats-by-vehicle-summary', input],
      queryFn: () => getNewFuelCardRequester().getTransactionStatsByVehicleSummary(input),
      enabled: options?.enabled ?? true,
    });
  };

  const useTransactionStatsByVehicleSummaryByPeriod = (input: TransactionSummaryBodyContentByPeriodType) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<TransactionStatsByVehicleSummaryByPeriod[]>({
      queryKey: [FUEL_TRANSACTIONS_QUERY_KEY, 'stats-by-vehicle-summary-by-period', input],
      queryFn: () => getNewFuelCardRequester().getTransactionStatsByVehicleSummaryByPeriod(input),
    });
  };

  const useTransactionsAverageFuelComsumptionAndCostPerKm = (input: TransactionSummaryBodyContentByPeriodType) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<TransactionsAvgFuelConsumptionAndCostPerKm[]>({
      queryKey: [FUEL_TRANSACTIONS_QUERY_KEY, 'avg-fuel-consumption-and-cost-per-km', input],
      queryFn: () => getNewFuelCardRequester().getTransactionsAvgFuelConsumptionAndCostPerKm(input),
    });
  };

  const useTransactionsSumTotalCostByFuelType = (input: TransactionSummaryBodyContentByPeriodType) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<TransactionsSumTotalCostByFuelType[]>({
      queryKey: [FUEL_TRANSACTIONS_QUERY_KEY, 'sum-total-cost-by-fuel-type', input],
      queryFn: () => getNewFuelCardRequester().getTransactionsSumTotalCostByFuelType(input),
    });
  };

  const useDeleteFuelCardAssociations = useMutation<void, unknown, { ids: UUID[] }>({
    mutationFn: (fuelCardDeleteContent) =>
      getGraphQLFuelCardRequester()
        .deleteFuelCardAssociation({ fuelCardIds: fuelCardDeleteContent.ids })
        .then(() => undefined),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [FUELCARD_ACTIVE_QUERY_KEY], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: [FUELCARD_INACTIVE_QUERY_KEY], refetchType: 'all' });
      toast.success(t('associations.delete_success', { count: variables.ids.length }));
      Analytics.track('Fuel Card Association Deleted', { count: variables.ids.length });
    },
    onError: () => toast.error(t('associations.delete_error')),
  });

  const useRestoreFuelCardAssociations = useMutation<void, unknown, { ids: UUID[] }>({
    mutationFn: (fuelCardRecoverContent) =>
      getGraphQLFuelCardRequester()
        .restoreFuelCardAssociation({ fuelCardIds: fuelCardRecoverContent.ids })
        .then(() => undefined),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [FUELCARD_ACTIVE_QUERY_KEY], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: [FUELCARD_INACTIVE_QUERY_KEY], refetchType: 'all' });
      toast.success(t('associations.restore_success', { count: variables.ids.length }));
      Analytics.track('Fuel Card Association Restored', { count: variables.ids.length });
    },
    onError: () => toast.error(t('associations.restore_error')),
  });

  const useImportTransactions = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation<void, unknown, { body: FuelTransactionBodyContent; odometerFile?: File }>({
      mutationFn: ({ body, odometerFile }) => {
        const api = getNewFuelCardRequester();
        const request = odometerFile ? api.createFuelTransactionWithOdometerFile(body, odometerFile) : api.createFuelTransaction(body);
        return request.then(() => undefined);
      },
      onSuccess: () => {
        invalidateFuelTransactionQueries(queryClient);
      },
    });
  };

  const deleteTransactions = useMutation<void, unknown, { transactionIds: string[] }>({
    mutationFn: (data) => getNewFuelCardRequester().deleteTransactions(data.transactionIds),
    onSuccess: () => {
      invalidateFuelTransactionQueries(queryClient);
    },
  });

  // Only "Conhecer cartão combustível" (`GET_TO_KNOW`) still posts here; integration requests go through `createIntegrationRequest`.
  const requestIntegration = useMutation<void, unknown, { provider: CardProviderOption; email?: string }>({
    mutationFn: (data) => getFuelCardRequester().requestIntegration(data.provider, data.email),
  });

  // T7's submit (FND-08, GF-835).
  const createIntegrationRequest = useMutation<IntegrationRequestResponse, unknown, CreateIntegrationRequestBody>({
    mutationFn: (body) => getNewFuelCardRequester().createIntegrationRequest(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FLEET_FUEL_INTEGRATIONS_QUERY_KEY });
    },
  });

  const updateFuelTransaction = useMutation<void, unknown, { transactionId: string; transactionData: UpdateTransactionRequest }>({
    mutationFn: (data) => getNewFuelCardRequester().updateFuelTransaction(data.transactionId, data.transactionData),
    onSuccess: () => {
      invalidateFuelTransactionQueries(queryClient);
    },
  });

  const useUpdateFuelCardAssociation = useMutation<void, unknown, { id: UUID; vehicleId?: string }>({
    mutationFn: (data) => {
      return getGraphQLFuelCardRequester()
        .updateFuelCardAssociation({ id: data.id, vehicleId: data.vehicleId })
        .then(() => undefined);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FUELCARD_ACTIVE_QUERY_KEY], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: [FUELCARD_INACTIVE_QUERY_KEY], refetchType: 'all' });
    },
  });

  const useValidateFuelTransactionsImport = (input: ValidateFuelTransactionsImportInput | null) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<ValidateFuelTransactionsImportResponse | undefined>({
      queryKey: [FUEL_TRANSACTIONS_QUERY_KEY, 'validate-import', input],
      queryFn: () => {
        if (!input) return undefined;
        return getGraphQLFuelCardRequester().validateFuelTransactionsImport(input);
      },
      enabled: !!input,
    });
  };

  const useBatchImportFuelTransactions = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation({
      mutationFn: (input: ImportFuelTransactionsBatchInput[]) => getGraphQLFuelCardRequester().importFuelTransactions(input),
      onSuccess: () => {
        invalidateFuelTransactionQueries(queryClient);
      },
      onError: () => toast.error(t('sheet_import.import_error')),
    });
  };

  /**
   * Sequentially creates each transaction via REST `POST /transactions/create`. Unlike the GraphQL
   * batch import, this path supports attaching the receipt image (base64) per transaction — used by
   * the photo-import flow.
   *
   * Partial-success semantics: items that succeed stay persisted (de-dupe relies on that), and per-item
   * failures are surfaced via `onItemProgress` so the UI can show which file worked and which didn't.
   * Callers receive the full `PhotoImportItemResult[]` so they can decide how to retry failed items.
   */
  const usePhotoImportFuelTransactions = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation<PhotoImportItemResult[], unknown, PhotoImportVariables>({
      mutationFn: async ({ inputs, odometerFiles, onItemProgress }) => {
        const api = getNewFuelCardRequester();
        const results: PhotoImportItemResult[] = [];

        for (let index = 0; index < inputs.length; index++) {
          const input = inputs[index];
          const odometerFile = odometerFiles?.[index];
          onItemProgress?.(index, { status: 'uploading' });
          try {
            const created = odometerFile ? await api.createFuelTransactionWithOdometerFile(input, odometerFile) : await api.createFuelTransaction(input);
            const result: PhotoImportItemResult = { ok: true, id: String(created.id) };
            results.push(result);
            onItemProgress?.(index, { status: 'success', id: result.id });
          } catch (error) {
            results.push({ ok: false, error });
            onItemProgress?.(index, { status: 'error', error });
          }
        }

        return results;
      },
      onSettled: () => {
        invalidateFuelTransactionQueries(queryClient);
      },
    });
  };

  const useUpdateFuelConsumptionGoals = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation({
      mutationFn: (data: UpsertVehicleFuelConsumptionGoalInput[]) => getGraphQLFuelCardRequester().batchSetFuelConsumptionGoals(data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [VEHICLES_QUERY_KEY] });
        toast.success(t('consumption_goal.update_success'));
      },
      onError: () => toast.error(t('consumption_goal.update_error')),
    });
  };

  const useDownloadTransactionsReport = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation<void, unknown, { period: DateRange; vehicleGroupIds: string[]; filters?: TransactionsReportFilters }>({
      mutationFn: async ({ period, vehicleGroupIds, filters }) => {
        const blob = await getNewFuelCardRequester().getTransactionsReport(period, vehicleGroupIds, filters);
        const startTimeStr = format(period.start, 'yyMMdd');
        const endTimeStr = format(period.end, 'yyMMdd');
        const periodStr = startTimeStr === endTimeStr ? startTimeStr : `${startTimeStr}_${endTimeStr}`;
        saveAs(blob, `Cobli_${periodStr}_transacoes.xlsx`);
      },
      onError: () => toast.error(t('report.export_error')),
    });
  };

  return {
    useFuelTransaction,
    useTransactionsHistory,
    useTransactionsSummary,
    useTransactionStatsByVehicle,
    useTransactionStatsByVehicleSummary,
    useTransactionStatsByVehicleSummaryByPeriod,
    useTransactionsAverageFuelComsumptionAndCostPerKm,
    useTransactionsSumTotalCostByFuelType,
    useGetActiveFuelCardAssociations,
    useGetInactiveFuelCardAssociations,
    useImportTransactions,
    useDeleteFuelCardAssociations,
    useRestoreFuelCardAssociations,
    deleteTransactions,
    requestIntegration,
    createIntegrationRequest,
    updateFuelTransaction,
    useUpdateFuelCardAssociation,
    useValidateFuelTransactionsImport,
    useBatchImportFuelTransactions,
    usePhotoImportFuelTransactions,
    useUpdateFuelConsumptionGoals,
    useDownloadTransactionsReport,
  };
};

export default useFuelCard;
